import { getToken } from './storage';
import { reportFetch, track } from './telemetry';
import type { SearchResponse, InventoryResponse, Settings, PriceHistoryResponse, PriceHistoryDuration, Edition, ScanResponse } from './types';

// API base URL - always use relative path since both dev and prod have /api proxy
const API_BASE = '/api';

// Default timeout for API requests (10 seconds)
const DEFAULT_TIMEOUT_MS = 10000;

// Declare global type for request timing callback
declare global {
  interface Window {
    __reportRequestTiming?: (durationMs: number) => void;
  }
}

class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

class TimeoutError extends Error {
  constructor(message: string = 'Request timed out') {
    super(message);
    this.name = 'TimeoutError';
  }
}

async function request<T>(
  endpoint: string,
  options: RequestInit & { timeout?: number; multipart?: boolean } = {}
): Promise<T> {
  const token = await getToken();

  if (!token) {
    // Token missing from IndexedDB - user needs to re-authenticate
    throw new ApiError(401, 'Session expired. Please log out and reconnect.');
  }

  const { multipart, ...fetchOptions } = options;

  const url = `${API_BASE}${endpoint}`;
  // Per-request UUID. Sent as X-Request-ID so the backend price-lookup
  // middleware can correlate its `backend_request` event with the frontend
  // `fetch` event (and any `api_error` from a non-2xx response).
  const requestId = crypto.randomUUID();
  // For multipart/form-data, omit Content-Type so the browser sets it with the
  // correct boundary. Setting it manually would break the multipart parse on the server.
  const headers: HeadersInit = {
    ...(multipart ? {} : { 'Content-Type': 'application/json' }),
    Authorization: `Bearer ${token}`,
    'X-Request-ID': requestId,
    ...options.headers,
  };

  // Set up timeout with AbortController
  const timeout = options.timeout ?? DEFAULT_TIMEOUT_MS;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  // Track request timing for network status detection
  const startTime = Date.now();
  const method = (options.method as string | undefined) ?? 'GET';

  let response: Response | null = null;
  let responseSize: number | undefined;
  try {
    response = await fetch(url, {
      ...fetchOptions,
      headers,
      signal: controller.signal,
    });

    const duration = Date.now() - startTime;
    if (typeof window !== 'undefined' && window.__reportRequestTiming) {
      window.__reportRequestTiming(duration);
    }

    if (!response.ok) {
      const text = await response.text();
      responseSize = text.length;
      if (!endpoint.startsWith('/telemetry')) {
        reportFetch({
          url: endpoint,
          status: response.status,
          duration_ms: duration,
          method,
          response_size: responseSize,
          request_id: requestId,
        });
        // Curated counterpart to the legacy `fetch` event so the dashboard's
        // errors feed has a single kind to query for failed API calls.
        track(
          'api_error',
          {
            endpoint,
            status: response.status,
            method,
            duration_ms: duration,
          },
          requestId,
        );
      }
      let message = 'Request failed';
      try {
        const json = JSON.parse(text);
        message = json.detail || json.message || message;
      } catch {
        message = text || message;
      }
      throw new ApiError(response.status, message);
    }

    const text = await response.text();
    responseSize = text.length;
    if (!endpoint.startsWith('/telemetry')) {
      reportFetch({
        url: endpoint,
        status: response.status,
        duration_ms: duration,
        method,
        response_size: responseSize,
        request_id: requestId,
      });
    }
    return JSON.parse(text) as T;
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      throw new TimeoutError(`Request to ${endpoint} timed out after ${timeout}ms`);
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

// Validate token by calling the /auth/me endpoint
// Returns: true = valid, false = definitely invalid (401), null = unknown (network error/timeout)
export async function validateToken(): Promise<boolean | null> {
  try {
    await request<{ id: string }>('/auth/me', { timeout: 5000 }); // 5 second timeout for auth
    return true;
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      return false; // Definitely invalid
    }
    if (error instanceof TimeoutError) {
      console.warn('Token validation timed out - assuming valid for offline use');
      return null; // Timeout - keep token, try again later
    }
    // Network errors are temporary - don't invalidate the token
    console.error('Token validation error (network):', error);
    return null; // Unknown - keep token, try again later
  }
}

// Search parameters for structured search
export interface SearchParams {
  /** Card name to search */
  name: string;
  /** Optional card number */
  number?: string;
  /** Optional Scrydex expansion ID */
  setId?: string;
}

// Search for cards using structured parameters
export async function searchCards(params: SearchParams): Promise<SearchResponse> {
  const queryParams = new URLSearchParams();
  queryParams.set('name', params.name);
  if (params.number) {
    queryParams.set('number', params.number);
  }
  if (params.setId) {
    queryParams.set('set_id', params.setId);
  }
  return request<SearchResponse>(`/price-lookup/search?${queryParams}`);
}

// Get inventory status for a card
export async function getInventory(
  setName: string,
  cardNumber: string
): Promise<InventoryResponse> {
  const params = new URLSearchParams({
    set_name: setName,
    card_number: cardNumber,
  });
  return request<InventoryResponse>(`/price-lookup/inventory-check?${params}`);
}

// Get settings from server
export async function getServerSettings(): Promise<Settings> {
  const data = await request<{
    buy_offer_percent: number;
    default_mode: 'buy' | 'sell';
  }>('/price-lookup/settings');

  return {
    buyOfferPercent: data.buy_offer_percent,
    defaultMode: data.default_mode,
  };
}

// Get price history for a card
export async function getPriceHistory(
  cardName: string,
  setName: string,
  cardNumber?: string,
  duration: PriceHistoryDuration = '30d',
  edition?: Edition | null
): Promise<PriceHistoryResponse> {
  const params = new URLSearchParams({
    card_name: cardName,
    set_name: setName,
    duration: duration,
  });
  if (cardNumber) {
    params.set('card_number', cardNumber);
  }
  if (edition) {
    params.set('edition', edition);
  }
  return request<PriceHistoryResponse>(`/price-lookup/price-history?${params}`, {
    timeout: 15000, // Allow longer timeout for external API call
  });
}

/**
 * Upload a card image for scan identification.
 *
 * Server resolves the image via Claude Vision + Scrydex and returns up to 3
 * candidate cards (same shape as the search response). A 30-second timeout
 * accounts for vision-call latency, which is notably slower than plain search.
 *
 * `intent` and `mode` are captured on the per-scan trace row server-side so
 * the admin scan-trace dashboard can filter by them. Optional — older callers
 * that don't pass them still work; the trace row just stores NULL there.
 */
export interface ScanCardOptions {
  intent?: 'quote' | 'add';
  mode?: 'buy' | 'sell';
  /** telemetry_sessions.id when the PWA has one open. */
  sessionId?: string;
}

export async function scanCard(
  imageBlob: Blob,
  options: ScanCardOptions = {}
): Promise<ScanResponse> {
  const form = new FormData();
  form.append('image', imageBlob, 'scan.jpg');
  if (options.intent) form.append('intent', options.intent);
  if (options.mode) form.append('mode', options.mode);
  if (options.sessionId) form.append('session_id', options.sessionId);
  return request<ScanResponse>('/price-lookup/scan', {
    method: 'POST',
    body: form,
    multipart: true,
    timeout: 30000,
  });
}

/**
 * Record the user's eventual pick (or cancel) for a scan trace.
 *
 * Called after `scan_candidate_picked` or `scan_cancelled` fires. Server
 * updates `picked_card_id` / `picked_index` on the trace row. Pass
 * `cardId=null, index=-1` to record a cancel. Best-effort — failures are
 * swallowed locally so the UI flow is never blocked by a trace write.
 */
export async function recordScanPick(
  requestId: string,
  cardId: string | null,
  index: number
): Promise<void> {
  try {
    await request<void>(
      `/telemetry/scans/${encodeURIComponent(requestId)}/pick`,
      {
        method: 'POST',
        body: JSON.stringify({ card_id: cardId, index }),
        timeout: 5000,
      }
    );
  } catch (err) {
    // Trace updates are forensic, not load-bearing. Surface in console only.
    console.warn('[scan-trace] pick update failed:', err);
  }
}

export { ApiError, TimeoutError };
